import { computed } from 'vue';
import { useStore } from 'vuex';
import { fireTag } from '@common-vue/util/src/modules/tagManager';
import { useBrand } from '@composables/useBrand';
import { usePageData } from '@composables/usePageData';
import { useKillswitches, useFeatureEligibility, useProductFlags } from '@modules/featureControl';
import { numOfAttributes, getUrlVars } from '@utils/commonUtils';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { useProductPrice } from '@modules/price/composables';
import { useProduct } from '@modules/product';
import { useType } from '@modules/types';
import { useSize } from '@modules/size/composables';
import { useColor } from '@modules/color/composables';
import { useTopLevelCategoryFlags } from '@modules/featureControl/composables/useTopLevelCategoryFlags';

import { SET_PAGE_UTAG_DATA } from '../../types/setters';
/**
 * @param {Product} product
 * @returns
 */
function getAvailabilityMessage(product) {
  const { availability } = product;
  if (!availability.available) return ['this product is currently unavailable'];
  return availability.lowAvailability
    ? [`just a few left: shipped by marketplace in ${availability.shipDays} days`]
    : [`Shipped by marketplace in ${availability.shipDays} days`];
}

// TODO: refactor to composable
/**
 * @param {Product} product
 */
function getProductData(product) {
  const {
    topLevelCategoryID, isCcfpAvailable, topLevelCategoryName, productId,
  } = useCommonProductGetters();

  return {
    topLevelCategoryID,
    ccfpEligible: isCcfpAvailable,
    topLevelCategoryName,
    marketPlaceSpecialHandling: product.detail.selectedUpcItem?.splHandlingAttrbs,
    pricingText: product.pricing?.price?.priceType?.text,
    registryData: product.registryData,
    imagesCount: product.imagery?.images?.length,
    pdpProductId: productId,
  };
}

/**
 * @param {number} productId
 * @param {Product} productData
 * @returns {number}
 * @description Determines how many interactive variants we display to customers. Interactive means that
 * type/size chips or color swatches are displayed and have multiple options which can be clicked on by customers.
 */
function getPdpVariantType(productId, productData) {
  const { types } = useType(productId);
  const { sizes } = useSize(productId, productData);
  const { colors } = useColor(productId);
  const { isMattressesCategory } = useTopLevelCategoryFlags();

  const variants = [
    types.value.length,
    sizes.value.length,
    isMattressesCategory ? 0 : colors.value.length,
  ];

  // if a product has more than 1 type/size/color, type/size chips & color swatches will render
  const variantType = variants.filter((variant) => variant > 1).length;

  return variantType;
}

function getDataLayerMcom(product, data, ks) {
  const {
    topLevelCategoryID,
    ccfpEligible,
    topLevelCategoryName,
    marketPlaceSpecialHandling,
    registryData,
    imagesCount,
  } = getProductData(product);
  // TODO: see if getProductData can be replaced with useProduct to get data needed
  const { productId, productData } = useProduct();
  const price = useProductPrice({ id: productId, product: productData });
  // TODO: product is missing registryData
  if (registryData && registryData.event_name === 'registry add') return product;
  const {
    isMarketPlaceEligibleFlag,
    isMasterProductFlag,
    homeFOBFlag,
    isBestSellerFlag,
    hasSizesFlag,
    hasTypesFlag,
    hasColorSwatchesFlag,
  } = useProductFlags();

  // TODO: missing validations: isStyliticsModal, styliticsNoShowTreatment, pdpStyliticsOutFittingWidgetEnabled
  const pdpOutfittingCap = computed(() => (!isMasterProductFlag && !homeFOBFlag ? ['true'] : ['false']));
  const videoEnabled = computed(() => data.product_video?.[0] === 'true');
  const { buyNow } = useFeatureEligibility();

  if (price.value.flags.isWithOfferPromo) {
    data.product_promo_code_flag = ['true'];
  } else {
    data.product_promo_code_flag = ['false'];
  }
  if (isMarketPlaceEligibleFlag) {
    data.product_availability_message = getAvailabilityMessage(product);
  }
  data.pdp_outfitting = pdpOutfittingCap.value;
  if (pdpOutfittingCap.value) {
    data.pdp_outfitting_type = 'wear it with';
  }
  data.is_responsive_pdp = 'true';
  data.eligible_attribute_count = numOfAttributes(product, hasColorSwatchesFlag, hasSizesFlag, hasTypesFlag);
  data.ccfp_eligible = ccfpEligible;
  data.topLevelCategoryId = topLevelCategoryID;
  data.topLevelCategoryName = topLevelCategoryName;
  const params = getUrlVars(window.location.href);
  data.prosContent = params.prosContent || null;
  data.recommendation_milanid = params.RecommendationMilanid || params.prosMilanId || 'no milanid';
  data.reommendation_model = params.RecommendationModel || params.prosModelId || '';
  data.recommendation_position = params.RecommendationPosition || params.prosPosition || '';
  data.recommendation_title = params.RecommendationTitle || params.prosTitle || '';
  data.recommendation_zone = params.RecommendationZone || params.prosZone || '';
  data.notification_msg = 'no pill';
  data.pdp_variant_type = getPdpVariantType(productId, productData);
  if (ks.isPdpProductEngagementPromptTrtTwoEnabled) {
    data.notification_msg = isBestSellerFlag ? 'best seller (pill)' : 'customers purchased (pill)';
  }

  data.product_numofimages = imagesCount;
  data.product_numofvideos = videoEnabled.value ? 1 : 0;
  data.marketPlaceItem = isMarketPlaceEligibleFlag;
  // TODO: product is missing detail.selectedUpcItem.splHandlingAttrbs
  data.marketPlaceSpecialHandling = marketPlaceSpecialHandling;

  // experiments
  if (buyNow) {
    data.experiment_key = 'pdpCondensedCheckout:1076-21:pdp buy now impression:06142023:07302023';
    data.experiment_key_type = 'condition';
  }

  return {
    ...data,
  };
}

function getDataLayerBcom(product, data) {
  const {
    isMarketPlaceEligibleFlag, isNewFlag, isMasterProductFlag, homeFOBFlag,
  } = useProductFlags();
  const {
    pdpProductId, ccfpEligible, topLevelCategoryID, pricingText,
  } = getProductData(product);
  const selectedPrice = useProductPrice();

  data.pdp_product_id = pdpProductId;
  data.ccfp_eligible = ccfpEligible;
  data.toplevel_category_id = topLevelCategoryID;
  data.marketPlaceItem = isMarketPlaceEligibleFlag;

  const { topOffer } = selectedPrice.value?.offersData || {};
  data.product_promo_messaging = topOffer ? [`${topOffer?.promoId}:${topOffer?.header}`] : ['no promo messaging'];

  const NEW_MARKDOWN = 'new markdown';
  data.product_badge_new_arrival = isNewFlag ? 'new arrival' : '';
  data.product_badge_new_markdown = pricingText?.indexOf(NEW_MARKDOWN) > -1 ? [NEW_MARKDOWN] : [''];

  if (isMarketPlaceEligibleFlag) {
    data.product_availability_message = getAvailabilityMessage(product);
  }
  // TODO: missing validations: isStyliticsModal, styliticsNoShowTreatment, pdpStyliticsOutFittingWidgetEnabled
  const pdpOutfittingCap = computed(() => (!isMasterProductFlag && !homeFOBFlag ? ['true'] : ['false']));
  data.pdp_outfitting = pdpOutfittingCap.value;
  if (pdpOutfittingCap.value) {
    data.pdp_outfitting_type = 'wear it with';
  }
  // TODO: missing validations: isStyliticsModal, styliticsNoShowTreatment, pdpStyliticsOutFittingWidgetEnabled

  return {
    ...data,
  };
}

function hasMultiplePriceType(colorMap = {}) {
  const priceTypeIds = Object.keys(colorMap).map((color) => colorMap[color]?.pricing?.price?.priceTypeId);
  return new Set(priceTypeIds).size > 1;
}

export function fireViewTag({ writeReviewPage } = {}) {
  const store = useStore();
  const { killswitches } = useKillswitches();
  const { productData } = useProduct();
  const pageData = usePageData();
  const { isMcom } = useBrand();
  const utagData = pageData.product?.analytics?.data || {};
  utagData.experimentation_ids = pageData.experimentIds;

  let pageUtagData = {};
  if (productData) {
    if (writeReviewPage) {
      utagData.page_type = 'responsive-review-page';
    }
    pageUtagData = isMcom
      ? getDataLayerMcom(productData, utagData, killswitches)
      : getDataLayerBcom(productData, utagData);
  } else if (isMcom) {
    pageUtagData = { ...pageUtagData, ...utagData };
    pageUtagData.page_type = 'product_unavailable';
    pageUtagData.error_message = 'Sorry, this product is no longer available for purchase';
  } else {
    pageUtagData = { ...pageUtagData, ...utagData };
    pageUtagData.page_type = 'error';
    pageUtagData.page_name = 'product_unavailable';
  }

  if (productData?.additionalFee) {
    pageUtagData.junk_fee_display = true;
  }

  const isMultiplePriceType = hasMultiplePriceType(productData?.traits?.colors?.colorMap);

  pageUtagData.product_pricing_state = isMultiplePriceType ? ['mixed'] : [utagData?.product_pricing_state?.[0]];

  const isAutoTaggingEnabled = pageData.product?.killswitches?.isAutoTaggingEnabled && pageData.experimentIds?.includes('1220-23');
  if (!isAutoTaggingEnabled) {
    fireTag('view', pageUtagData);
  }

  // Storing utagData so it can be use again from Product Quickview
  // TODO: Should explore moving this whole logic to server/pageData.js
  store.commit(SET_PAGE_UTAG_DATA, pageUtagData);
}

export default { fireViewTag };
